import formStyles from "./UserCreationForm.module.css"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-plus.svg"
import { ButtonStatus, CompanyStatus, CompanyType, FileType } from "../common/constants"
import { DialogStatus } from "../enums"
import StatusBadge from "./StatusBadge"
import Button from "./Button"
import AlertDialog from "./dialogs/AlertDialog"
import Dialog from "./dialogs/Dialog"
import MeatBallsMenu from "./MeatBallsMenu"
import Select from "./Select"
import Table from "./Table"
import TextInput from "./TextInput"
import Uploader from "./Uploader"
import styles from "./Companies.module.css"

const Companies = forwardRef(({ orderBy, sortDirection }, ref) => {
    const navigate = useNavigate()
    const [companies, setCompanies] = useState([])
    const [filteredCompanies, setFilteredCompanies] = useState([])
    const [loading, setLoading] = useState(false)

    const [open, setOpen] = useState(false)
    const [editingCompany, setEditingCompany] = useState(null)
    const [submitStatus, setSubmitStatus] = useState(null)
    const [logoUrl, setLogoUrl] = useState(null)
    const [uploaderReset, setUploaderReset] = useState(false)
    const [openDeleteAlert, setOpenDeleteAlert] = useState(false)
    const [deleteStatus, setDeleteStatus] = useState(DialogStatus.Default)
    const [companyToDelete, setCompanyToDelete] = useState(null)

    const typeOptions = useMemo(() => [
        { id: 0, name: "B2B Teaching", value: CompanyType.B2BTeaching },
        { id: 1, name: "Lead Generation Provider", value: CompanyType.LeadGenerationProvider },
        { id: 2, name: "Training Buyer", value: CompanyType.TrainingBuyer },
        { id: 3, name: "Event Partner", value: CompanyType.EventPartner },
        { id: 4, name: "Other", value: CompanyType.Other },
    ], [])

    const statusOptions = useMemo(() => [
        { id: 0, name: "Attiva", value: CompanyStatus.Active },
        { id: 1, name: "Inattiva", value: CompanyStatus.Inactive },
    ], [])

    const [name, setName] = useState("")
    const [selectedType, setSelectedType] = useState(typeOptions[0])
    const [selectedStatus, setSelectedStatus] = useState(statusOptions[0])
    const [email, setEmail] = useState("")
    const [phone, setPhone] = useState("")
    const [website, setWebsite] = useState("")
    const [vatNumber, setVatNumber] = useState("")

    const loadCompanies = useCallback(async () => {
        setLoading(true)
        try {
            const response = await api.get(`/admin/companies?page=0&per=500`)
            const list = (response?.companies || []).sort((a, b) => {
                const dir = sortDirection === "" ? 1 : -1
                const key = orderBy?.value || "created_at"
                if (key === "created_at") {
                    return (new Date(a.created_at) - new Date(b.created_at)) * dir
                }
                return String(a?.[key] || "").localeCompare(String(b?.[key] || "")) * dir
            })
            setCompanies(list)
            setFilteredCompanies(list)
        } catch (e) {
            console.error(e)
        }
        setLoading(false)
    }, [orderBy, sortDirection])

    useEffect(() => {
        loadCompanies()
    }, [loadCompanies])

    const resetDialog = () => {
        setOpen(false)
        setEditingCompany(null)
        setSubmitStatus(null)
        setLogoUrl(null)
        setUploaderReset((prev) => !prev)
        setName("")
        setSelectedType(typeOptions[0])
        setSelectedStatus(statusOptions[0])
        setEmail("")
        setPhone("")
        setWebsite("")
        setVatNumber("")
    }

    const onAddClick = () => {
        setEditingCompany(null)
        setName("")
        setLogoUrl(null)
        setUploaderReset((prev) => !prev)
        setSelectedType(typeOptions[0])
        setSelectedStatus(statusOptions[0])
        setEmail("")
        setPhone("")
        setWebsite("")
        setVatNumber("")
        setOpen(true)
    }

    const openCompanyPage = useCallback((company) => {
        if (!company?.id) return
        navigate(`/users/companies/${company.id}`)
    }, [navigate])

    const onEditClick = (company) => {
        openCompanyPage(company)
    }

    const onDeleteClick = (company) => {
        setCompanyToDelete(company)
        setDeleteStatus(DialogStatus.Default)
        setOpenDeleteAlert(true)
    }

    const confirmDeleteCompany = async () => {
        if (!companyToDelete) return

        setDeleteStatus(DialogStatus.Loading)
        try {
            await api.delete(`/admin/companies/${companyToDelete.id}`)
            await loadCompanies()
            setDeleteStatus(DialogStatus.Success)
        } catch (e) {
            console.error(e)
            setDeleteStatus(DialogStatus.Error)
        }
    }

    const onDeleteDialogClose = () => {
        setOpenDeleteAlert(false)
        setDeleteStatus(DialogStatus.Default)
        setCompanyToDelete(null)
    }

    const onSearch = useCallback((value) => {
        const text = value?.trim()?.toLowerCase() || ""
        if (!text) {
            setFilteredCompanies(companies)
            return
        }

        setFilteredCompanies(
            companies.filter((company) => {
                const fields = [
                    company?.name,
                    company?.email,
                    company?.phone,
                    company?.vat_number,
                    company?.website,
                    company?.type,
                ]
                return fields.some((field) => String(field || "").toLowerCase().includes(text))
            })
        )
    }, [companies])

    const saveCompany = async () => {
        if (!name.trim()) return

        setSubmitStatus(ButtonStatus.Loading)
        const payload = {
            name: name.trim(),
            type: selectedType.value,
            status: selectedStatus.value,
            logo: logoUrl || null,
            email: email.trim() || null,
            phone: phone.trim() || null,
            website: website.trim() || null,
            vat_number: vatNumber.trim() || null,
        }

        try {
            if (editingCompany) {
                await api.put(`/admin/companies/${editingCompany.id}`, payload)
            } else {
                await api.post(`/admin/companies`, payload)
            }
            setSubmitStatus(ButtonStatus.Success)
            resetDialog()
            await loadCompanies()
        } catch (e) {
            console.error(e)
            setSubmitStatus(ButtonStatus.Error)
        }
    }

    const getCompanyTypeLabel = (type) => {
        return typeOptions.find((option) => option.value === type)?.name || type
    }

    const getCompanyStatusBadge = (status) => {
        switch (status) {
            case CompanyStatus.Active:
                return <StatusBadge appearance="soft" label={"ATTIVA"} color={"var(--sf-green)"} />
            case CompanyStatus.Inactive:
                return <StatusBadge appearance="soft" label={"INATTIVA"} color={"var(--sf-orange)"} />
            default:
                return null
        }
    }

    const getCompanyActions = (company) => {
        if (!company) return []

        return [
            {
                label: "Apri",
                onClick: () => onEditClick(company),
            },
            {
                label: "Elimina",
                style: { color: "var(--sf-red)" },
                onClick: () => onDeleteClick(company),
            },
        ]
    }

    useImperativeHandle(ref, () => ({
        onAddClick,
    }))

    return (
        <div className={styles.container}>
            <div className={styles.section}>
                <div className={styles.sectionInner}>
                    <div className={styles.companies}>
                        <div className={styles.searchBar}>
                        <TextInput
                            onKeyUp={onSearch}
                            type="search" appearance="modern"
                            placeholder="Cerca azienda"
                            style={{ backgroundColor: "var(--background-secondary-color)" }}
                        />

                        </div>
                        <Table appearance="minimal" style={{ borderRadius: 0, marginTop: 0 }}
                            columns={[
                                { key: "id", label: "ID", type: "number" },
                                { key: "logo", label: "LOGO", type: "custom" },
                                { key: "name", label: "NOME", type: "string" },
                                { key: "type", label: "TIPOLOGIA", type: "string" },
                                { key: "email", label: "EMAIL", type: "string" },
                                { key: "phone", label: "TELEFONO", type: "string" },
                                { key: "status", label: "STATO", type: "custom" },
                                { key: "created_at", label: "CREATA IL", type: "datetime" },
                                { key: "actions", label: "AZIONI", type: "custom", handleClick: true },
                            ]}
                            loading={loading}
                            onRowClick={openCompanyPage}
                            elements={filteredCompanies.map((company) => ({
                                ...company,
                                logo: {
                                    value: company.logo,
                                    type: "string",
                                    component: company.logo ? <img src={company.logo} alt={company.name} className={styles.logo} /> : "N/A",
                                },
                                type: getCompanyTypeLabel(company.type),
                                status: { value: company.status, type: "string", component: getCompanyStatusBadge(company.status) },
                                actions: {
                                    value: company.id,
                                    type: "string",
                                    component: <MeatBallsMenu actions={getCompanyActions(company)} style={{ padding: "0.5rem" }} />,
                                },
                            }))}
                            defaultSort={{ key: "created_at", direction: "desc" }}
                        />
                    </div>
                </div>
            </div>

            <Dialog
                appearance="modern"
                style={{ maxWidth: 840 }}
                open={open}
                onClose={resetDialog}
                title={editingCompany ? "Modifica azienda" : "Aggiungi azienda"}
            >
                <div className={formStyles.dialog}>
                    <div className={formStyles.imageSelector}>
                        <Uploader
                            crop={true}
                            shape="square"
                            aspect={1}
                            type={FileType.Image}
                            multiple={false}
                            reset={uploaderReset}
                            preview={logoUrl}
                            message="Trascina o seleziona il logo aziendale"
                            label="Scegli logo"
                            iconButtons={true}
                            onComplete={(uploads) => setLogoUrl(uploads?.[0]?.url || null)}
                            onDelete={() => setLogoUrl(null)}
                        />
                    </div>

                    <div className={formStyles.inputs}>
                        <TextInput appearance="modern" value={name} type="text" label="Nome azienda" placeholder="Nome azienda" onKeyUp={setName} />
                        <div className={formStyles.field}><span className={formStyles.label}>Tipo di azienda</span><Select appearance="modern" portal selected={selectedType} options={typeOptions} onSelect={setSelectedType} /></div>
                        <div className={formStyles.field}><span className={formStyles.label}>Stato</span><Select appearance="modern" portal selected={selectedStatus} options={statusOptions} onSelect={setSelectedStatus} /></div>
                        <TextInput appearance="modern" value={email} type="text" label="Email" placeholder="Email" onKeyUp={setEmail} />
                        <TextInput appearance="modern" value={phone} type="text" label="Telefono" placeholder="Telefono" onKeyUp={setPhone} />
                        <TextInput appearance="modern" value={website} type="text" label="Sito web" placeholder="Sito web" onKeyUp={setWebsite} />
                        <TextInput appearance="modern" value={vatNumber} type="text" label="Partita IVA" placeholder="Partita IVA" onKeyUp={setVatNumber} />

                        <div className={formStyles.bottom}>
                            <Button
                                style={{ padding: '.65rem 1rem', borderRadius: 8, fontSize: '.75rem' }}
                                accentColor={"var(--primary)"}
                                disabled={!name.trim() || submitStatus === ButtonStatus.Loading}
                                status={submitStatus}
                                onClick={saveCompany}
                            >
                                {editingCompany ? "Salva" : "Aggiungi"}
                                {submitStatus === null && !editingCompany && <AddIcon />}
                            </Button>
                        </div>
                    </div>
                </div>
            </Dialog>

            <AlertDialog
                open={openDeleteAlert}
                title={deleteStatus === DialogStatus.Success ? "Azienda eliminata" : "Elimina azienda"}
                text={deleteStatus === DialogStatus.Error
                    ? "Impossibile eliminare l'azienda. Verifica che non abbia admin associati."
                    : `Sei sicuro di voler eliminare ${companyToDelete?.name || "questa azienda"}?`
                }
                status={deleteStatus}
                onClose={onDeleteDialogClose}
                actions={deleteStatus === DialogStatus.Success
                    ? [{ label: "OK", onClick: onDeleteDialogClose }]
                    : [
                        { label: "ANNULLA", onClick: onDeleteDialogClose, inverse: true },
                        { label: "SI, ELIMINA", onClick: confirmDeleteCompany },
                    ]
                }
            />
        </div>
    )
})

export default Companies
